import {
  BitkitBadge,
  BitkitCheckbox,
  BitkitControlButton,
  BitkitTable,
  BitkitTag,
  BitkitTooltip,
  IconAndroid,
  IconApple,
  IconDetails,
  IconLaptop,
  rem,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { LinkBox, LinkOverlay } from '@chakra-ui/react/link';
import { Text } from '@chakra-ui/react/text';
import { useContext } from 'react';

import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { BuildCacheToolIds } from '@/models/BuildCacheTool';
import { BuildToolInvocationWithDetails } from '@/models/BuildToolInvocation';
import { isLocalBuildEnvironment } from '@/models/CiProvider';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import CacheStatusCell from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/CacheStatusCell';
import DurationCell from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/DurationCell';
import { useInvocationListTableContext } from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationListTableContext';
import InvocationStatusIcon from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationStatusIcon';
import { hidesCacheStatusColumn } from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/utils/invocationCacheStatus';
import { formatDateTime } from '@/utils/commonFormats';
import { buildToolInvocationPath } from '@/utils/monolithApiRouteService';

// The desktop badge is icon-only, so this doubles as its accessible name — the tooltip alone only
// contributes aria-describedby, which leaves a focusable element with nothing to announce.
const LOCAL_BUILD_ENVIRONMENT_LABEL = 'Build environment: local';

interface InvocationListTableRowDesktopProps {
  invocation: BuildToolInvocationWithDetails;
  onOpenDrawer: (invocation: BuildToolInvocationWithDetails) => void;
}

const InvocationListTableRowDesktop = ({ invocation, onOpenDrawer }: InvocationListTableRowDesktopProps) => {
  const {
    account: { slug: workspaceSlug },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const {
    isBuildSpecific: isTableBuildSpecific,
    isProjectSpecific: isTableProjectSpecific,
    isInSelectMode,
    invocationsToCompare,
    setInvocationsToCompare,
    selectedTool,
  } = useInvocationListTableContext();
  const hideProjectColumn = isTableBuildSpecific || isTableProjectSpecific;
  const isReactNative = selectedTool === BuildCacheToolIds.reactNative;
  const hideCacheStatusColumn = hidesCacheStatusColumn(selectedTool);
  const isLocal = isLocalBuildEnvironment(invocation.ciProvider);

  const selectedToCompare = invocationsToCompare.some(
    (anInvocation) => anInvocation.invocationId === invocation.invocationId,
  );

  const compareCheckboxLabel = `Select ${invocation.shortCommand || invocation.command} started at ${formatDateTime(
    invocation.startedAt,
    { hideSeconds: true, hideYear: true },
  )} for comparison`;

  const handleSelectToCompare = (isSelected: boolean) => {
    if (setInvocationsToCompare) {
      if (isSelected) {
        setInvocationsToCompare([...invocationsToCompare, invocation]);
      } else {
        setInvocationsToCompare(
          invocationsToCompare.filter((anInvocation) => anInvocation.invocationId !== invocation.invocationId),
        );
      }
    }
  };

  return (
    // transform gives the row a containing block, so the LinkOverlay's stretched ::before resolves
    // against it — a bare `position: relative` on a `<tr>` is not enough in every browser.
    <LinkBox as={BitkitTable.Row} transform="scale(1)" css={{ '& td:first-of-type': { textAlign: 'center' } }}>
      <BitkitTable.Cell>
        {isInSelectMode ? (
          <InvocationStatusIcon status={invocation.status} />
        ) : (
          <LinkOverlay href={buildToolInvocationPath(workspaceSlug, invocation.invocationId, invocation.tool)}>
            <InvocationStatusIcon status={invocation.status} />
          </LinkOverlay>
        )}
      </BitkitTable.Cell>
      {!hideProjectColumn && (
        <BitkitTable.Cell
          title={invocation.projectTitle}
          overflowX="hidden"
          textOverflow="ellipsis"
          minWidth={rem(200)}
          maxWidth="0"
          width="full"
        >
          <Box display="flex" alignItems="center" gap="8">
            <Text as="span" textStyle="body/md/regular" truncate>
              {invocation.projectTitle}
            </Text>
            {isLocal && (
              <BitkitTooltip text={LOCAL_BUILD_ENVIRONMENT_LABEL}>
                <BitkitBadge
                  aria-label={LOCAL_BUILD_ENVIRONMENT_LABEL}
                  colorVariant="blue"
                  icon={IconLaptop}
                  position="relative"
                  role="img"
                  tabIndex={0}
                  zIndex="1"
                />
              </BitkitTooltip>
            )}
          </Box>
        </BitkitTable.Cell>
      )}
      <BitkitTable.Cell title={invocation.command} overflowX="hidden" textOverflow="ellipsis" maxWidth={rem(280)}>
        {invocation.shortCommand || invocation.command}
      </BitkitTable.Cell>
      {isReactNative && (
        <BitkitTable.Cell>
          <Box display="flex" gap="8">
            {invocation.targetOs?.map((os) => (
              <BitkitTag
                key={os}
                icon={os === 'ios' ? IconApple : IconAndroid}
                labelText={os === 'ios' ? 'iOS' : 'Android'}
                size="sm"
              />
            ))}
          </Box>
        </BitkitTable.Cell>
      )}
      {!hideCacheStatusColumn && (
        <BitkitTable.Cell>
          <CacheStatusCell invocation={invocation} />
        </BitkitTable.Cell>
      )}
      <BitkitTable.Cell textAlign="end">
        <DurationCell invocation={invocation} />
      </BitkitTable.Cell>
      <BitkitTable.Cell>{formatDateTime(invocation.startedAt, { hideSeconds: true, hideYear: true })}</BitkitTable.Cell>
      {!isTableBuildSpecific && (
        <BitkitTable.Cell>
          {isInSelectMode && setInvocationsToCompare ? (
            <Box onClick={(e) => e.stopPropagation()}>
              <BitkitCheckbox
                checked={selectedToCompare}
                aria-label={compareCheckboxLabel}
                onCheckedChange={({ checked }) => handleSelectToCompare(checked === true)}
                paddingInline="4"
                state={!selectedToCompare && invocationsToCompare.length > 1 ? 'disabled' : undefined}
              />
            </Box>
          ) : (
            <BitkitControlButton
              size="md"
              icon={IconDetails}
              label="Invocation details"
              onClick={(e) => {
                e.stopPropagation();
                (e.currentTarget as HTMLElement).blur();
                onOpenDrawer(invocation);
              }}
              marginLeft="16"
              padding="4"
              position="relative"
            />
          )}
        </BitkitTable.Cell>
      )}
    </LinkBox>
  );
};

export default InvocationListTableRowDesktop;
